import Decimal from 'decimal.js';
import useLoyaltyPointConversionRates from '../datastore/useLoyaltyPointConversionRates';
import { OrderItemStatus, PaymentType, PaymentTypeStr, ProductCategoryType } from './enums';
import { calculateGSTBreakdown } from './moneyFunctions';
import { calculateTotalPlusSurcharge } from './surchargeFunctions';
import {
  Order,
  OrderItem,
  OrderItemQtyMap,
  OrderItemWithQty,
  OrderPayment,
  OrderSummary,
  Product,
  ProductVariant,
  StaffOrderItem,
  StaffSelectedVariant,
  WithQty
} from './types';

export const isItemType = (orderItem: StaffOrderItem, categories: ProductCategoryType[]): boolean => {
  return categories.includes(orderItem.productCategoryType as ProductCategoryType)
}

export function getOrderItemKey(item: OrderItem | StaffOrderItem): string {
  // Sort variants for consistent grouping
  if (item.selectedVariants?.length > 0) {
    return [
      item.productName,
      item.productSizeId,
      ...item.selectedVariants
        .map(
          (variant) =>
            (variant as ProductVariant)?.name ||
            (variant as StaffSelectedVariant)?.productVariant.name,
        )
        .sort((a, b) => a.localeCompare(b)),
      item.notes || '',
    ].join('|');
  }
  return [
    item.productName,
    item.productSizeId,
    item.notes || '',
  ].join('|');
}

export function groupOrderItems(
  items: OrderItem[],
  justByProductName = false,
): OrderItemWithQty[] {
  if (justByProductName) {
    // for hot drinks opposing station
    const mapped = items.reduce<OrderItemQtyMap>((out, item) => {
      if (!out[item.productName]) {
        out[item.productName] = { ...item, quantity: 0 };
      }
      out[item.productName].quantity += 1;
      return out;
    }, {});
    return Object.values(mapped).sort((a, b) =>
      a.productName.localeCompare(b.productName),
    );
  }

  // now we have to deal with matching everything down to comments
  const mapped = items.reduce<OrderItemQtyMap>((out, item) => {
    const key = getOrderItemKey(item);
    if (!out[key]) {
      out[key] = { ...item, quantity: 0 };
    }
    out[key].quantity += 1;
    return out;
  }, {});
  return Object.values(mapped).sort((a, b) => {
    const nameCheck = a.productName.localeCompare(b.productName);
    if (nameCheck) {
      return nameCheck;
    }
    return a.quantity - b.quantity;
  });
}

type StaffOrderItemWithQty = WithQty<StaffOrderItem>;
type StaffOrderItemWithQtyMap = Record<string, StaffOrderItemWithQty>;

export function groupStaffOrderItems(
  items: StaffOrderItem[],
  justByProductName = false,
): StaffOrderItemWithQty[] {
  if (justByProductName) {
    // for hot drinks opposing station
    const mapped = items.reduce<StaffOrderItemWithQtyMap>((out, item) => {
      if (!out[item.productName]) {
        out[item.productName] = { ...item, quantity: 0 };
      }
      out[item.productName].quantity += 1;
      return out;
    }, {});
    return Object.values(mapped).sort((a, b) =>
      a.productName.localeCompare(b.productName),
    );
  }

  // now we have to deal with matching everything down to comments
  const mapped = items.reduce<StaffOrderItemWithQtyMap>((out, item) => {
    const key = getOrderItemKey(item);
    if (!out[key]) {
      out[key] = { ...item, quantity: 0 };
    }
    out[key].quantity += 1;
    return out;
  }, {});
  return Object.values(mapped).sort((a, b) => {
    const nameCheck = a.productName.localeCompare(b.productName);
    if (nameCheck) {
      return nameCheck;
    }
    return a.quantity - b.quantity;
  });
}

export const mapProductToOrderItem = (product: Product): OrderItem => {
  const defaultSize =
    product.productSizes.find((ps) => ps.isDefault) || product.productSizes[0];

  const defaultVariants = product.productVariants.filter(
    (f) => f.isActive && f.isCore,
  );

  const item: OrderItem = {
    id: 0,
    productId: product.id,
    productName: product.name,
    queueOrder: 0,
    productPrice: product.price,
    productSizeId: defaultSize.id,
    sizeOptions: product.productSizes.filter((f) => f.isActive),
    productCategoryType: product.productCategory.name,
    selectedVariants: defaultVariants,
    notes: '',
    status: OrderItemStatus.Placed,
    allowedVariants: product.productVariants.filter((f) => f.isActive),
  };

  return item;
};

export const mapOrderSummaryToOrder = (
  orderSummary: OrderSummary,
  products: Product[] = [],
): Partial<Order> => {
  const newOrder: Partial<Order> = {};
  newOrder.siteId = orderSummary.siteId;
  newOrder.requestedPickupTime = new Date();
  const mappedOrderItems: OrderItem[] = [];

  for (const orderItem of orderSummary.items) {
    const product = products.find((f) => f.id === orderItem.productId);
    if (!product) {
      throw new Error('missing product');
    }
    const newOrderItem = mapProductToOrderItem(product);
    newOrderItem.productSizeId =
      product.productSizes.find((f) => f.sizeName === orderItem.sizeName)?.id ||
      0;
    newOrderItem.selectedVariants = [];
    for (const variant of orderItem.items) {
      const matchingVariant: ProductVariant | undefined =
        newOrderItem.allowedVariants.find((f) => f.id === variant.variantId);
      if (!matchingVariant) {
        throw new Error('missing product variant');
      } else {
        for (let i = 0; i < variant.quantity; i++) {
          newOrderItem.selectedVariants = [
            ...newOrderItem.selectedVariants,
            matchingVariant,
          ];
        }
      }
    }
    for (let i = 0; i < orderItem.quantity; i++) {
      mappedOrderItems.push(newOrderItem);
    }
    newOrder.orderItems = mappedOrderItems;
  }

  return newOrder;
};

type OrderPaymentBreakdown = {
  total: Decimal;
  subtotal: Decimal;
  gst: Decimal;
  surcharge: Decimal;
  totalPlusSurcharge: Decimal | null;
  loyaltyPointsCoverDollars: Decimal;
  loyaltyPointsToReserve: Decimal;
  balance: Decimal;
}


export const calculateOrderPaymentBreakdown = (
  orderItems: OrderItemWithQty[],
  paymentType: PaymentTypeStr,
  surchargeFixed: number,
  surchargePercentage: number,
  availableLoyaltyPoints: number,
  lpConversionRate: number
): OrderPaymentBreakdown => {
  const { total, subtotal, gst } = calculateGSTBreakdown(orderItems);
  const { calculateDollarsCovered, calculatePointsConsumed } = useLoyaltyPointConversionRates.getState();
  let loyaltyPointsCoverDollars = new Decimal(lpConversionRate > 0 ? calculateDollarsCovered(availableLoyaltyPoints, lpConversionRate) : 0);
  if (loyaltyPointsCoverDollars.greaterThan(total)) {
    loyaltyPointsCoverDollars = total
  }
  let loyaltyPointsToReserve = new Decimal(lpConversionRate > 0 ? calculatePointsConsumed(loyaltyPointsCoverDollars.toNumber(), lpConversionRate) : 0)
  if (loyaltyPointsToReserve.greaterThan(new Decimal(0)) && loyaltyPointsToReserve.greaterThan(new Decimal(availableLoyaltyPoints))) {
    loyaltyPointsToReserve = new Decimal(availableLoyaltyPoints)
  }
  const balance = total.minus(loyaltyPointsCoverDollars)
  if (paymentType === PaymentTypeStr.LoyaltyPoints) {
    return {
      total,
      subtotal,
      gst,
      surcharge: new Decimal(0),
      totalPlusSurcharge: total,
      loyaltyPointsCoverDollars,
      loyaltyPointsToReserve,
      balance,
    };
  } else if (paymentType === PaymentTypeStr.Stripe) {
    const totalPlusSurcharge = calculateTotalPlusSurcharge(total, surchargeFixed, surchargePercentage);
    const surcharge = totalPlusSurcharge !== null ? totalPlusSurcharge.minus(total) : new Decimal(0)
    return {
      total,
      subtotal,
      gst,
      surcharge,
      totalPlusSurcharge,
      loyaltyPointsCoverDollars,
      loyaltyPointsToReserve,
      balance,
    };
  } else {
    // card / cash / defer
    return {
      total,
      subtotal,
      gst,
      surcharge: new Decimal(0),
      totalPlusSurcharge: total,
      loyaltyPointsCoverDollars,
      loyaltyPointsToReserve,
      balance: new Decimal(0),
    };
  }
}

const newOrderPayment = (pt: PaymentType, amount: number, orderId: number): OrderPayment => ({
  paymentType: pt,
  id: 0,
  orderId: orderId || 0,
  amount,
  createdAt: new Date()
})

export const getOrderPayments = (paymentType: PaymentTypeStr, total: Decimal, loyaltyPointsCoverDollars: Decimal, balance: Decimal, orderId: number = 0): OrderPayment[] => {
  const orderPayments: OrderPayment[] = []
  switch (paymentType) {
    case PaymentTypeStr.Stripe:
      orderPayments.push(newOrderPayment(PaymentType.Stripe, total.toNumber(), orderId ))
      break;
    case PaymentTypeStr.LoyaltyPoints:
      orderPayments.push(newOrderPayment(PaymentType.LoyaltyPoints, loyaltyPointsCoverDollars.toNumber(), orderId ))
      if (balance.greaterThan(0)) {
        orderPayments.push(newOrderPayment(PaymentType.Defer, balance.toNumber(), orderId ))
      }
      break;
    case PaymentTypeStr.Cash:
      orderPayments.push(newOrderPayment(PaymentType.Cash, total.toNumber(), orderId ))
      break;
    case PaymentTypeStr.Card:
      orderPayments.push(newOrderPayment(PaymentType.Card, total.toNumber(), orderId ))
      break;
    default:
      orderPayments.push(newOrderPayment(PaymentType.Defer, total.toNumber(), orderId ))
      break;
  }
  return orderPayments
}